import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Upload, 
  Palette, 
  Type, 
  Image as ImageIcon, 
  X, 
  Building2,
  PenTool,
  FileText,
  LayoutTemplate,
} from 'lucide-react';
import type { CertificateTemplate } from './CertificatePreview';

interface CertificateEditorProps {
  template: CertificateTemplate;
  onChange: (template: CertificateTemplate) => void;
  onLogoUpload: (file: File) => Promise<string | null>;
  onSignatureUpload: (file: File) => Promise<string | null>;
  uploading?: boolean;
}

export function CertificateEditor({
  template,
  onChange,
  onLogoUpload,
  onSignatureUpload,
  uploading = false,
}: CertificateEditorProps) {
  const [dragOverLogo, setDragOverLogo] = useState(false);
  const [dragOverSig, setDragOverSig] = useState(false);

  const updateTemplate = useCallback(
    (updates: Partial<CertificateTemplate>) => {
      onChange({ ...template, ...updates });
    },
    [template, onChange]
  );

  const handleFileUpload = async (
    file: File,
    type: 'logo' | 'signature'
  ) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) return; // 5MB max

    if (type === 'logo') {
      const url = await onLogoUpload(file);
      if (url) updateTemplate({ logoUrl: url });
    } else {
      const url = await onSignatureUpload(file);
      if (url) updateTemplate({ signatureUrl: url });
    }
  };

  const handleDrop = (
    e: React.DragEvent,
    type: 'logo' | 'signature'
  ) => {
    e.preventDefault();
    if (type === 'logo') setDragOverLogo(false);
    else setDragOverSig(false);
    
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file, type);
  };

  const handleFileInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'logo' | 'signature'
  ) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, type);
  };

  return (
    <ScrollArea className="h-[calc(100vh-220px)]">
      <div className="space-y-6 pr-4 pb-4">
        {/* Company Branding Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Company Branding
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Company Logo Upload */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Company Logo</Label>
              {template.logoUrl ? (
                <div className="relative inline-block">
                  <img
                    src={template.logoUrl}
                    alt="Company Logo"
                    className="h-16 w-auto max-w-[200px] object-contain rounded border p-1"
                  />
                  <button
                    onClick={() => updateTemplate({ logoUrl: undefined })}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/90"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                    dragOverLogo
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-primary/50'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverLogo(true);
                  }}
                  onDragLeave={() => setDragOverLogo(false)}
                  onDrop={(e) => handleDrop(e, 'logo')}
                  onClick={() =>
                    document.getElementById('logo-upload')?.click()
                  }
                >
                  <Upload className="w-6 h-6 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    {uploading ? 'Uploading...' : 'Drop logo here or click to upload'}
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    PNG, JPG, SVG up to 5MB
                  </p>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileInput(e, 'logo')}
                  />
                </div>
              )}
            </div>

            {/* Logo Position */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Logo Position</Label>
              <Select
                value={template.logoPosition}
                onValueChange={(v) =>
                  updateTemplate({
                    logoPosition: v as CertificateTemplate['logoPosition'],
                  })
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top-left">Top Left</SelectItem>
                  <SelectItem value="top-center">Top Center</SelectItem>
                  <SelectItem value="top-right">Top Right</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Company Name */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Company Name</Label>
              <Input
                value={template.companyName || ''}
                onChange={(e) =>
                  updateTemplate({ companyName: e.target.value })
                }
                placeholder="Your Company Name"
                className="h-9"
              />
            </div>
          </CardContent>
        </Card>

        {/* Certificate Content Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Certificate Content
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Certificate Title */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Certificate Title</Label>
              <Input
                value={template.certificateTitle || ''}
                onChange={(e) =>
                  updateTemplate({ certificateTitle: e.target.value })
                }
                placeholder="Certificate of Completion"
                className="h-9"
              />
            </div>

            {/* Issuer Name */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Issuer Name</Label>
              <Input
                value={template.issuerName || ''}
                onChange={(e) =>
                  updateTemplate({ issuerName: e.target.value })
                }
                placeholder="John Smith"
                className="h-9"
              />
            </div>

            {/* Issuer Title */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Issuer Title</Label>
              <Input
                value={template.issuerTitle || ''}
                onChange={(e) =>
                  updateTemplate({ issuerTitle: e.target.value })
                }
                placeholder="Director of Training"
                className="h-9"
              />
            </div>

            {/* Accreditation Text */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Accreditation Text</Label>
              <Input
                value={template.accreditationText || ''}
                onChange={(e) =>
                  updateTemplate({ accreditationText: e.target.value })
                }
                placeholder="Accredited by..."
                className="h-9"
              />
            </div>

            {/* Footer Text */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Footer Text</Label>
              <Input
                value={template.footerText || ''}
                onChange={(e) =>
                  updateTemplate({ footerText: e.target.value })
                }
                placeholder="© 2025 Your Company. All rights reserved."
                className="h-9"
              />
            </div>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <PenTool className="w-4 h-4" />
              Signature
            </CardTitle>
          </CardHeader>
          <CardContent>
            {template.signatureUrl ? (
              <div className="relative inline-block">
                <img
                  src={template.signatureUrl}
                  alt="Signature"
                  className="h-12 w-auto max-w-[200px] object-contain rounded border p-1 bg-white"
                />
                <button
                  onClick={() => updateTemplate({ signatureUrl: undefined })}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/90"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  dragOverSig
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverSig(true);
                }}
                onDragLeave={() => setDragOverSig(false)}
                onDrop={(e) => handleDrop(e, 'signature')}
                onClick={() =>
                  document.getElementById('sig-upload')?.click()
                }
              >
                <ImageIcon className="w-6 h-6 mx-auto mb-1 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  {uploading ? 'Uploading...' : 'Drop signature image or click'}
                </p>
                <input
                  id="sig-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileInput(e, 'signature')}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Style & Colors Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Colors & Style
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Primary Color */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Primary Color</Label>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="color"
                    value={template.primaryColor}
                    onChange={(e) =>
                      updateTemplate({ primaryColor: e.target.value })
                    }
                    className="w-10 h-9 rounded border cursor-pointer bg-transparent p-0.5"
                  />
                </div>
                <Input
                  value={template.primaryColor}
                  onChange={(e) =>
                    updateTemplate({ primaryColor: e.target.value })
                  }
                  placeholder="#000000"
                  className="h-9 font-mono text-xs flex-1"
                />
              </div>
            </div>

            {/* Accent Color */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Accent Color</Label>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="color"
                    value={template.accentColor}
                    onChange={(e) =>
                      updateTemplate({ accentColor: e.target.value })
                    }
                    className="w-10 h-9 rounded border cursor-pointer bg-transparent p-0.5"
                  />
                </div>
                <Input
                  value={template.accentColor}
                  onChange={(e) =>
                    updateTemplate({ accentColor: e.target.value })
                  }
                  placeholder="#000000"
                  className="h-9 font-mono text-xs flex-1"
                />
              </div>
            </div>

            {/* Background Color */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Background Color</Label>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="color"
                    value={template.backgroundColor || '#ffffff'}
                    onChange={(e) =>
                      updateTemplate({ backgroundColor: e.target.value })
                    }
                    className="w-10 h-9 rounded border cursor-pointer bg-transparent p-0.5"
                  />
                </div>
                <Input
                  value={template.backgroundColor || ''}
                  onChange={(e) =>
                    updateTemplate({
                      backgroundColor: e.target.value || undefined,
                    })
                  }
                  placeholder="Default (auto)"
                  className="h-9 font-mono text-xs flex-1"
                />
                {template.backgroundColor && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 px-2"
                    onClick={() =>
                      updateTemplate({ backgroundColor: undefined })
                    }
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>

            <Separator />

            {/* Font Family */}
            <div className="space-y-2">
              <Label className="text-xs font-medium flex items-center gap-1">
                <Type className="w-3 h-3" />
                Font Family
              </Label>
              <Select
                value={template.fontFamily}
                onValueChange={(v) =>
                  updateTemplate({
                    fontFamily: v as CertificateTemplate['fontFamily'],
                  })
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="serif">Serif (Classic)</SelectItem>
                  <SelectItem value="sans">Sans-Serif (Clean)</SelectItem>
                  <SelectItem value="elegant">Elegant (Italic)</SelectItem>
                  <SelectItem value="modern">Modern (Geometric)</SelectItem>
                  <SelectItem value="handwritten">Handwritten (Casual)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Border Style */}
            <div className="space-y-2">
              <Label className="text-xs font-medium flex items-center gap-1">
                <LayoutTemplate className="w-3 h-3" />
                Border Style
              </Label>
              <Select
                value={template.borderStyle}
                onValueChange={(v) =>
                  updateTemplate({
                    borderStyle: v as CertificateTemplate['borderStyle'],
                  })
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ornate">Ornate (Double thick)</SelectItem>
                  <SelectItem value="double">Double</SelectItem>
                  <SelectItem value="simple">Simple</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </ScrollArea>
  );
}
